import { gacTokens } from './tokens.generated';

/**
 * The storefront theme is assembled from the design tokens, not written by hand:
 * `tokens.generated.ts` comes from GAC-stacks/infra `design/tokens/tokens.json`
 * and the guard test proves the two still agree. Anything below that is NOT a
 * token is marked "retired" — those are values the brand book dropped and the
 * screens still use; they leave as tracks 1–2 rewrite the screens.
 */
const dark = gacTokens.theme.dark;

export const SPACING_UNIT = gacTokens.space.step;

export const defaultTheme = {
  colors: {
    // surfaces and text
    black: dark.surface.base,
    white: dark.text.primary,
    lightWhite: dark.text.secondary,
    mutedWhite: dark.text.muted,
    disabledWhite: dark.text.disabled,
    surfaceRaised: dark.surface.raised,
    surfaceOverlay: dark.surface.overlay,
    surfaceHover: dark.surface.hover,
    border: dark.line.border,
    divider: dark.line.divider,
    scrim: dark.scrim,

    // accent — 10% of the screen at most, and the text on it is always black
    accent: gacTokens.brand.accent,
    accentHover: gacTokens.brand.accentHover,
    accentInk: gacTokens.brand.accentInk,

    // status — a label's text and border, never a filled block
    success: gacTokens.status.success,
    info: gacTokens.status.info,
    warning: gacTokens.status.warning,
    danger: gacTokens.status.danger,

    transparent: 'transparent',

    // retired: not tokens, kept until the screens that use them are rewritten.
    // `red` is a second name for danger; `grey` is Typography's default colour;
    // `extraLightWhite` is a button fill with no token behind it.
    red: gacTokens.status.danger,
    grey: '#010101',
    extraLightWhite: 'rgba(255, 255, 255, 0.2)',
  },

  /**
   * Eight steps, each with the face and weight the brand book gives it: 32–20
   * are Big Shoulders (headings and every number), 17 and below are Public Sans.
   * The legacy names are kept so existing screens keep compiling.
   */
  typography: {
    xLargeTitle: gacTokens.typography.titleXl,
    largeTitle: gacTokens.typography.titleLg,
    title1: gacTokens.typography.titleMd,
    title3: gacTokens.typography.titleSm,
    heading: gacTokens.typography.heading,
    large: gacTokens.typography.body,
    medium: gacTokens.typography.caption,
    small: gacTokens.typography.eyebrow,

    // retired: 22/28 and 10/12 are not in the brand book's scale. Screens move to
    // the nearest step (title1 / small) as tracks 1–2 touch them.
    title2: { ...gacTokens.typography.titleMd, fontSize: 22, lineHeight: 28 },
    xSmall: { ...gacTokens.typography.eyebrow, fontSize: 10, lineHeight: 12 },
  },

  borderRadius: {
    small: gacTokens.radius.sm,
    medium: gacTokens.radius.md,
    large: gacTokens.radius.lg,
    round: gacTokens.radius.pill,

    // retired: 36 is not a token — a panel is either 32 or 16.
    xLarge: 36,
  },

  /** One set for every front: below sm one column, then two, three, four. */
  breakpoints: gacTokens.breakpoint,

  space: gacTokens.space,
  radius: gacTokens.radius,
  shadows: gacTokens.shadow,
  fonts: gacTokens.font.family,
  fontWeights: gacTokens.font.weight,
  control: gacTokens.control,
};

/** `${media.md} { … }` inside a styled-component. Mobile first: min-width only. */
export const media = {
  sm: `@media (min-width: ${gacTokens.breakpoint.sm}px)`,
  md: `@media (min-width: ${gacTokens.breakpoint.md}px)`,
  lg: `@media (min-width: ${gacTokens.breakpoint.lg}px)`,
} as const;

export type Theme = typeof defaultTheme;

export type ColorVariant = keyof typeof defaultTheme.colors;

export type TypographyVariant = keyof typeof defaultTheme.typography;
